import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ImageMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: () => void;
  onSubmitAsStock: () => void;
  // onCreateMovingPortrait: () => void;
  // onCreatePortraitWithSameAvatar: () => void;
  className?: string;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  isMobileView?: boolean;
}

// Mobile Modal Components
function ExpandIndicator() {
  return (
    <div className="box-border content-stretch flex flex-col gap-2.5 items-center justify-center px-[174px] py-2.5 relative size-full">
      <div className="bg-[rgba(37,54,88,0.15)] h-1 rounded-[3px] shrink-0 w-[26px]" />
    </div>
  );
}

function HomeIndicator() {
  return (
    <div className="relative size-full">
      <div className="absolute bg-black bottom-2 h-[5px] rounded-[100px] translate-x-[-50%] w-[134px]" style={{ left: "calc(50% + 0.5px)" }} />
    </div>
  );
}

const ImageMenu: React.FC<ImageMenuProps> = ({
  isOpen,
  onClose,
  onDownload,
  onSubmitAsStock,
  // onCreateMovingPortrait,
  // onCreatePortraitWithSameAvatar,
  className = '',
  buttonRef,
  isMobileView = false
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: -1000, left: -1000 });

  // Update menu position when opened (desktop only)
  useEffect(() => {
    if (isOpen && buttonRef?.current && !isMobileView) {
      const updatePosition = () => {
        const rect = buttonRef.current!.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + 4,
          left: rect.right - 235 // Align to right edge of button, accounting for menu width
        });
      };
      
      updatePosition();
      
      // Update position on scroll and resize
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, buttonRef, isMobileView]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target as Node) &&
        buttonRef?.current && 
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen && !isMobileView) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef, isMobileView]);

  if (!isOpen) return null;

  // Mobile Modal View
  if (isMobileView) {
    const mobileModal = (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-end">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-[rgba(0,0,0,0.6)] backdrop-blur-[5px]"
          onClick={onClose}
        />
        
        {/* Modal Content */}
        <div className="relative bg-white w-full rounded-tl-[15px] rounded-tr-[15px] max-h-[80vh] flex flex-col">
          {/* Top Bar */}
          <div className="flex flex-col items-start justify-start">
            <div className="w-full flex items-center justify-center py-2.5">
              <ExpandIndicator />
            </div>
            <div className="w-full px-4 pb-4">
              <div className="flex items-center justify-center w-full">
                <h3 className="font-['Inter'] font-bold text-[18px] text-[#253658]">
                  Image Options
                </h3>
              </div>
            </div>
          </div>

          {/* Menu Options */}
          <div className="flex-1 overflow-y-auto px-4 pb-5">
            <div className="space-y-2">
              <button
                onClick={() => {
                  onDownload();
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-1 py-3 hover:bg-gray-50 transition-colors border-b border-[rgba(37,54,88,0.08)] last:border-b-0"
              >
                <svg className="w-5 h-5 text-[#253658]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-['Inter'] font-normal text-[14px] text-[#253658]">
                  Download Image
                </span>
              </button>
              
              <button
                onClick={() => {
                  onSubmitAsStock();
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-1 py-3 hover:bg-gray-50 transition-colors border-b border-[rgba(37,54,88,0.08)] last:border-b-0"
              >
                <svg className="w-5 h-5 text-[#253658]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="font-['Inter'] font-normal text-[14px] text-[#253658]">
                  Submit as Stock Avatar
                </span>
              </button>
              
              {/* <button
                onClick={() => {
                  onCreateMovingPortrait();
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-1 py-3 hover:bg-gray-50 transition-colors border-b border-[rgba(37,54,88,0.08)] last:border-b-0"
              >
                <svg className="w-5 h-5 text-[#253658]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5 4a1 1 0 00-.894 1.447L5 7v11a1 1 0 001.447.894L10 17l3.553 1.894A1 1 0 0015 18V7l.894-1.553A1 1 0 0015 4H5z" />
                </svg>
                <span className="font-['Inter'] font-normal text-[14px] text-[#253658]">
                  Create Moving Portrait
                </span>
              </button> */}
              
              {/* <button
                onClick={() => {
                  onCreatePortraitWithSameAvatar();
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-1 py-3 hover:bg-gray-50 transition-colors border-b border-[rgba(37,54,88,0.08)] last:border-b-0"
              >
                <svg className="w-5 h-5 text-[#253658]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  <circle cx="15" cy="3" r="1" fill="currentColor" />
                  <circle cx="17" cy="5" r="0.5" fill="currentColor" />
                  <circle cx="13" cy="6" r="0.5" fill="currentColor" />
                </svg>
                <span className="font-['Inter'] font-normal text-[14px] text-[#253658]">
                  Create Portrait with Same Avatar
                </span>
              </button> */}
            </div>
          </div>

          {/* Home Indicator */}
          <div className="h-[34px] relative w-full">
            <HomeIndicator />
          </div>
        </div>
      </div>
    );
    
    return createPortal(mobileModal, document.body);
  }

  // Desktop View - Portal Menu
  const portalMenu = (
    <div
      ref={menuRef}
      className={`
        fixed bg-white rounded-lg shadow-lg border border-gray-200
        py-2 min-w-[200px] z-[9998]
        ${className}
      `}
      style={{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`
      }}
    >
      <button
        onClick={() => {
          onDownload();
          onClose();
        }}
        className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Download Image
      </button>
      
      <button
        onClick={() => {
          onSubmitAsStock();
          onClose();
        }}
        className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        Submit as Stock Avatar
      </button>
      
      {/* <button
        onClick={() => {
          onCreateMovingPortrait();
          onClose();
        }}
        className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M5 4a1 1 0 00-.894 1.447L5 7v11a1 1 0 001.447.894L10 17l3.553 1.894A1 1 0 0015 18V7l.894-1.553A1 1 0 0015 4H5z" />
        </svg>
        Create Moving Portrait
      </button> */}
      
      {/* <button
        onClick={() => {
          onCreatePortraitWithSameAvatar();
          onClose();
        }}
        className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          <circle cx="15" cy="3" r="1" fill="currentColor" />
          <circle cx="17" cy="5" r="0.5" fill="currentColor" />
          <circle cx="13" cy="6" r="0.5" fill="currentColor" />
        </svg>
        Create Portrait with Same Avatar
      </button> */}
    </div>
  );

  return createPortal(portalMenu, document.body);
};

export default ImageMenu;